import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HistoryIcon, TriangleAlertIcon } from 'lucide-react';

import { ApiError, api, type ModuleChangePreview, type ModuleDetail, type ScoringConfig } from '@/lib/api';
import { useT, useTn } from '@/lib/i18n';
import { notify } from '@/lib/notify';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { ReadinessReport } from './readiness';
import { ConfigForm } from './config-form';

/**
 * The scoring-config editor: a **structured form**, a **raw-JSON escape hatch**,
 * a **diff before saving** and a **version history**.
 *
 * WHY A PREVIEW STEP AND NOT JUST A SAVE. Editing a config is the one catalogue
 * act the cascade-killer does not make safe. A scoring-semantic edit mints a
 * version, and every in-flight run stays pinned to the one it launched with —
 * which is correct, and is also invisible. "Save" therefore asks the server what
 * it would do first, and the answer includes the fact nothing else can give
 * back: **how many runs are pinned to the version about to be superseded**.
 *
 * ### The form and the JSON are ONE DRAFT, not two editors
 *
 * `09 §5` asks for structured editors *with* a JSON escape hatch, and the
 * obvious way to build that — two tabs each with their own state — is how they
 * end up disagreeing about what is being saved. So there is exactly one draft
 * string. The form parses it, hands `ConfigForm` a config object, and writes
 * whatever comes back out as JSON again; the textarea edits the same string
 * directly. Switching tabs carries every edit across because there is nothing to
 * carry. Both clear the preview, for the reason stated at the bottom of this
 * file.
 *
 * The form appears only when the module's plugin describes its config surface
 * (`configFields` on `/api/plugins`). A model that does not gets the JSON tab
 * alone, which is what every model had before T5 — the round trip below is still
 * the load-bearing part, and the server runs the module's own plugin on preview
 * and on save whichever tab was used.
 *
 * VERSION IS NOT EDITABLE. It is derived server-side and bumps only on a
 * scoring-semantic change; a renamed label must not bump it.
 */

function DiffRow({ entry }: { entry: ModuleChangePreview['configDiff'][number] }) {
  const t = useT();
  const render = (value: unknown) =>
    value === undefined ? t('config.absent') : JSON.stringify(value);

  return (
    <li className="space-y-1 border-s-2 ps-3 text-xs">
      <div className="flex items-center gap-2">
        <span className="font-mono font-medium">{entry.field}</span>
        {entry.scoringSemantic ? (
          <Badge variant="secondary">{t('config.changesScores')}</Badge>
        ) : (
          <Badge variant="outline">{t('config.displayOnly')}</Badge>
        )}
      </div>
      <p className="text-muted-foreground truncate font-mono">− {render(entry.from)}</p>
      <p className="truncate font-mono">+ {render(entry.to)}</p>
    </li>
  );
}

/**
 * What the server refused, verbatim.
 *
 * Extracted so the form tab shows exactly what the JSON tab shows. These are the
 * module plugin's own `validateConfig` strings — the form can prevent some of
 * what they describe and is not the authority on any of it, so the tab that
 * looks safer must not be the tab that hides the rejection.
 */
function RejectionAlert({ errors }: { errors: string[] }) {
  const t = useT();
  return (
    <Alert variant="destructive" role="alert">
      <AlertDescription>
        <p className="mb-1 font-medium">{t('config.rejected')}</p>
        <ul className="list-inside list-disc">
          {errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}

/** The diff, the version bump and what is pinned to the outgoing version. */
function PreviewReport({ preview }: { preview: ModuleChangePreview }) {
  const t = useT();
  const tn = useTn();

  return (
    <div className="space-y-3">
      {preview.configDiff.length === 0 ? (
        <Alert>
          <AlertDescription>{t('config.identical')}</AlertDescription>
        </Alert>
      ) : (
        <>
          <ul className="space-y-2">
            {preview.configDiff.map((entry) => (
              <DiffRow key={entry.field} entry={entry} />
            ))}
          </ul>

          {preview.versionBump.bumps ? (
            <Alert>
              <TriangleAlertIcon className="size-4" />
              <AlertDescription>
                <p className="font-medium">
                  {t('config.versions', {
                    from: preview.versionBump.from,
                    to: preview.versionBump.to,
                  })}
                </p>
                <p className="text-muted-foreground mt-1 text-xs">
                  {tn('config.pinnedOpen', preview.pinnedToCurrent.open, {
                    version: preview.versionBump.from,
                  })}{' '}
                  {tn('config.pinnedCompleted', preview.pinnedToCurrent.completed)}
                </p>
              </AlertDescription>
            </Alert>
          ) : (
            <Alert>
              <AlertDescription>
                {t('config.displayOnlyChange', { version: preview.versionBump.from })}
              </AlertDescription>
            </Alert>
          )}
        </>
      )}

      <ReadinessReport readiness={preview.readiness} />
    </div>
  );
}

function VersionHistory({ module }: { module: ModuleDetail }) {
  const t = useT();
  const history = module.scoringConfigHistory ?? [];

  if (history.length === 0) {
    return (
      <Alert>
        <HistoryIcon className="size-4" />
        <AlertDescription>
          {t('config.noHistory', { version: module.scoringConfig.version })}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-sm">{t('config.history.hint')}</p>
      {[...history].reverse().map((config: ScoringConfig) => (
        <details key={config.version} className="rounded-md border p-3">
          <summary className="cursor-pointer text-sm font-medium">
            v{config.version}
            <span className="text-muted-foreground ms-2 font-normal">
              {t('config.dimensionsBands', {
                dimensions: config.dimensions.length,
                bands: config.bands.length,
              })}
            </span>
          </summary>
          <pre className="mt-2 max-h-64 overflow-auto text-xs">{JSON.stringify(config, null, 2)}</pre>
        </details>
      ))}
    </div>
  );
}

export function ConfigEditor({ moduleId, onClose }: { moduleId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const t = useT();
  const detail = useQuery({ queryKey: ['module', moduleId], queryFn: () => api.module(moduleId) });

  const [draft, setDraft] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ModuleChangePreview | null>(null);

  /**
   * The registry, for this module's own plugin descriptor, and the persona list
   * the norms editor names its tables after.
   *
   * Both are catalogue reads an analyst may make, and both are cheap and cached
   * by react-query — the module list screen has already fetched personas by the
   * time anybody opens this dialog. Neither gates the editor: a plugin call that
   * fails leaves `fields` null and the JSON tab is the whole editor, which is a
   * worse experience and not a broken one.
   */
  const plugins = useQuery({ queryKey: ['plugins'], queryFn: api.plugins });
  const personas = useQuery({ queryKey: ['personas'], queryFn: api.personas });

  const text = draft ?? (detail.data ? JSON.stringify(detail.data.scoringConfig, null, 2) : '');

  /**
   * The draft as an object, or `null` if it does not parse.
   *
   * The form can only render a config it can read, so a JSON tab left in a
   * half-typed state disables the form tab rather than throwing inside it. The
   * message says which, because "the form is unavailable" with no reason is the
   * kind of thing an operator retries three times.
   */
  let parsedDraft: ScoringConfig | null = null;
  try {
    parsedDraft = JSON.parse(text) as ScoringConfig;
  } catch {
    parsedDraft = null;
  }

  // `?.items?.` — the registry is a nice-to-have here, and a body that is not
  // the shape this expects must cost the FORM, not the whole dialog. Without
  // the second `?.` a malformed `/api/plugins` response throws inside render
  // and takes the JSON editor down with it, which is the one part of this
  // dialog that has never needed the registry at all.
  const fields =
    plugins.data?.items?.find((p) => p.modelType === detail.data?.modelType)?.configFields ?? null;

  /**
   * Every write goes through here, whichever tab made it.
   *
   * Serialising the form's object back into the SAME draft string the textarea
   * edits is what makes the two tabs one editor. `null` for the preview is not
   * incidental — see the note above the return.
   */
  function writeDraft(next: ScoringConfig) {
    setDraft(JSON.stringify(next, null, 2));
    setPreview(null);
  }

  /** Parse the editor, or report why it will not parse. Never throws. */
  function readDraft(): ScoringConfig | null {
    setParseError(null);
    try {
      return JSON.parse(text) as ScoringConfig;
    } catch (err) {
      setParseError(err instanceof Error ? err.message : t('config.notJson'));
      return null;
    }
  }

  /** The server returns field-level reasons from the plugin's own validateConfig. */
  function showFailure(err: unknown) {
    const details = err instanceof ApiError ? (err.payload.details as { errors?: string[] } | undefined) : undefined;
    setErrors(details?.errors ?? [err instanceof Error ? err.message : t('config.requestFailed')]);
  }

  const check = useMutation({
    mutationFn: (config: ScoringConfig) => api.previewModule(moduleId, { scoringConfig: config }),
    onSuccess: (report) => {
      setErrors([]);
      setPreview(report);
    },
    onError: (err) => {
      setPreview(null);
      showFailure(err);
    },
  });

  const save = useMutation({
    mutationFn: (config: ScoringConfig) => api.updateModule(moduleId, { scoringConfig: config }),
    onSuccess: (updated) => {
      setErrors([]);
      notify.success(
        t('config.saved'),
        t('config.saved.detail', { version: updated.scoringConfig.version }),
      );
      void queryClient.invalidateQueries({ queryKey: ['module', moduleId] });
      void queryClient.invalidateQueries({ queryKey: ['modules'] });
      onClose();
    },
    onError: showFailure,
  });

  /**
   * Save is gated on a preview, and every keystroke clears the preview.
   *
   * Not ceremony: the preview is the only place the pinned-run count appears,
   * and a save that skipped it would be the version bump this dialog exists to
   * make visible, made invisible again by an impatient click. Clearing on edit
   * is what stops a preview of one config authorising the save of another.
   */
  return (
    <DialogContent className="sm:max-w-3xl">
      <DialogHeader>
        <DialogTitle>{t('config.title', { module: moduleId })}</DialogTitle>
        <DialogDescription>{t('config.description')}</DialogDescription>
      </DialogHeader>

      {detail.isLoading && <Skeleton className="h-72 w-full" />}

      {detail.data && (
        <Tabs defaultValue={fields ? 'form' : 'edit'}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <TabsList>
              {/* Only when the plugin describes its surface. A form tab that
                  opened onto nothing would be worse than no tab. */}
              {fields && <TabsTrigger value="form">{t('configForm.tab')}</TabsTrigger>}
              <TabsTrigger value="edit">{t('configForm.json')}</TabsTrigger>
              <TabsTrigger value="history">
                {t('config.versionHistory')}
                {(detail.data.scoringConfigHistory?.length ?? 0) > 0 && (
                  <Badge variant="secondary" className="ms-2">
                    {detail.data.scoringConfigHistory.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="secondary">{detail.data.modelType}</Badge>
              <Badge variant="outline">
                {t('modules.configVersion', { version: detail.data.scoringConfig.version })}
              </Badge>
              <Badge variant="outline">
                {t('modules.pluginVersion', { version: detail.data.pluginVersion })}
              </Badge>
            </div>
          </div>

          {fields && (
            <TabsContent value="form" className="max-h-[60vh] space-y-3 overflow-y-auto pe-1">
              {parsedDraft ? (
                <ConfigForm
                  config={parsedDraft}
                  fields={fields}
                  personas={personas.data?.items ?? []}
                  onChange={writeDraft}
                />
              ) : (
                <Alert variant="destructive" role="alert">
                  <AlertDescription>{t('configForm.unparseable')}</AlertDescription>
                </Alert>
              )}

              {/* The same two blocks the JSON tab shows, because the round trip
                  is the same round trip. A form that swallowed the server's
                  field-level rejection would be a form that looks safer than
                  the textarea while being exactly as capable of writing a
                  config the plugin refuses. */}
              {errors.length > 0 && <RejectionAlert errors={errors} />}
              {preview && <PreviewReport preview={preview} />}
            </TabsContent>
          )}

          <TabsContent value="edit" className="space-y-3">
            <Textarea
              value={text}
              onChange={(e) => {
                setDraft(e.target.value);
                // Any edit invalidates the preview it was not computed from.
                setPreview(null);
              }}
              spellCheck={false}
              className="h-64 font-mono text-xs"
              aria-label={t('config.jsonLabel')}
            />

            {parseError && (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{t('config.notJsonDetail', { error: parseError })}</AlertDescription>
              </Alert>
            )}

            {errors.length > 0 && <RejectionAlert errors={errors} />}
            {preview && <PreviewReport preview={preview} />}
          </TabsContent>

          <TabsContent value="history">
            <VersionHistory module={detail.data} />
          </TabsContent>
        </Tabs>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            const config = readDraft();
            if (config) check.mutate(config);
          }}
          disabled={check.isPending || !detail.data}
        >
          {check.isPending ? t('config.checking') : t('config.preview')}
        </Button>
        <Button
          onClick={() => {
            const config = readDraft();
            if (config) save.mutate(config);
          }}
          disabled={save.isPending || !detail.data || preview === null}
          title={preview === null ? t('config.previewFirst') : undefined}
        >
          {save.isPending ? t('common.saving') : t('config.save')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
